<template>
    <div class="sales-horizon-chart">
        <div class="row g-2 mb-3">
            <div class="col">
                <div class="sales-horizon-chart__stat rounded-2 h-100">
                    <div class="small text-muted text-nowrap">{{ chart.totalLabel }}</div>
                    <div class="fw-semibold">{{ chart.total }}</div>
                </div>
            </div>
            <template v-if="chart.saleModes.length > 1">
                <div v-for="(saleMode, index) in chart.saleModes" :key="saleMode.days" class="col">
                    <div class="sales-horizon-chart__stat rounded-2 h-100">
                        <div class="small text-muted text-nowrap">
                            <span class="sales-horizon-chart__symbol sales-horizon-chart__symbol--column" :style="{background: toneOf(index)}"></span>{{ saleMode.days }}
                        </div>
                        <div class="fw-semibold">{{ saleMode.share }} %</div>
                        <div class="small text-muted text-nowrap">{{ saleMode.note }}</div>
                    </div>
                </div>
            </template>
        </div>
        <div class="sales-horizon-chart__canvas">
            <canvas ref="canvas"></canvas>
        </div>
        <div class="sales-horizon-chart__legend d-flex flex-wrap justify-content-center mt-2">
            <span class="text-nowrap"><span class="sales-horizon-chart__symbol sales-horizon-chart__symbol--estimate"></span>{{ chart.averageNote }}</span>
            <span class="text-nowrap">{{ chart.range }}</span>
        </div>
    </div>
</template>
<script lang="ts">
import {NEARBY_SALES_COLORS, NearbySalesChartData, salesAxis, toneOf} from '@/components/charts/nearbySales';
import {formatSales} from '@/components/charts/salesHorizon';
import {Chart, Plugin} from 'chart.js';
import {defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

/**
 * The chart of the sales of similar estates nearby of a real estate estimate: tiles with the share of every sale mode,
 * which are also the legend, columns of the sales by the time of sale and their average as a dashed line; the picture
 * of the documents (GdNearbySalesChartRenderer) draws the same numbers. The font is the one of the page.
 */
export default defineComponent({
    props: {
        chart: {
            type: Object as PropType<NearbySalesChartData>,
            required: true,
        },
    },
    setup(props) {
        const canvas = ref<HTMLCanvasElement | null>(null);
        let instance: Chart<'bar', number[], string> | null = null;

        onMounted(() => {
            const element = canvas.value as HTMLCanvasElement;
            const font = getComputedStyle(element).fontFamily;
            const data = props.chart;
            const axis = salesAxis(data);

            // The total of a column above it, on white over the line of the average as the picture of the documents
            const totals: Plugin<'bar'> = {
                id: 'nearbySalesTotals',
                afterDatasetsDraw(chart) {
                    const context = chart.ctx;
                    context.save();
                    context.textAlign = 'center';
                    context.font = `600 11px ${font}`;
                    data.periods.forEach((period, index) => {
                        if (period.total > 0) {
                            const x = chart.scales.x.getPixelForValue(index);
                            const y = chart.scales.y.getPixelForValue(period.total) - 5;
                            const text = String(period.total);
                            const width = context.measureText(text).width;
                            context.fillStyle = '#ffffff';
                            context.fillRect(x - width / 2 - 2, y - 11, width + 4, 14);
                            context.fillStyle = NEARBY_SALES_COLORS.ink;
                            context.fillText(text, x, y);
                        }
                    });
                    context.restore();
                },
            };

            const average: Plugin<'bar'> = {
                id: 'nearbySalesAverage',
                afterDatasetsDraw(chart) {
                    const y = chart.scales.y.getPixelForValue(data.average);
                    const {left, right} = chart.chartArea;
                    const context = chart.ctx;
                    context.save();
                    context.beginPath();
                    context.moveTo(left, y);
                    context.lineTo(right, y);
                    context.lineWidth = 2;
                    context.strokeStyle = NEARBY_SALES_COLORS.average;
                    context.setLineDash([7, 5]);
                    context.stroke();
                    context.restore();
                },
            };

            instance = new Chart<'bar', number[], string>(element, {
                type: 'bar',
                data: {
                    labels: data.periods.map(period => period.label),
                    datasets: data.saleModes.map((saleMode, index) => ({
                        label: saleMode.days,
                        data: data.periods.map(period => period.sales[index]),
                        backgroundColor: toneOf(index),
                        hoverBackgroundColor: toneOf(index),
                        borderColor: '#ffffff',
                        borderWidth: {top: 1},
                        borderSkipped: false,
                        maxBarThickness: 70,
                        categoryPercentage: 0.7,
                        barPercentage: 0.9,
                    })),
                },
                plugins: [average, totals],
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {duration: 600},
                    layout: {padding: {top: 18}},
                    font: {family: font},
                    interaction: {mode: 'index', intersect: false},
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            backgroundColor: 'rgba(23, 28, 40, 0.92)',
                            padding: 12,
                            cornerRadius: 8,
                            displayColors: false,
                            titleFont: {family: font, weight: '600', size: 12},
                            bodyFont: {family: font, size: 13},
                            callbacks: {
                                title: items => `${data.periods[items[0].dataIndex].name}: ${formatSales(data.periods[items[0].dataIndex].total, data.offerType)}`,
                                label: item => `${data.saleModes[item.datasetIndex].days}: ${item.raw}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            stacked: true,
                            grid: {display: false, drawBorder: false},
                            ticks: {color: NEARBY_SALES_COLORS.muted, font: {family: font, size: 12}},
                        },
                        y: {
                            stacked: true,
                            min: 0,
                            max: axis.max,
                            grid: {color: NEARBY_SALES_COLORS.grid, drawBorder: false},
                            ticks: {color: NEARBY_SALES_COLORS.muted, stepSize: axis.step, font: {family: font, size: 12}},
                        },
                    },
                },
            });
        });

        onBeforeUnmount(() => {
            instance?.destroy();
            instance = null;
        });

        return {canvas, toneOf};
    },
});
</script>
